/**
 * Enriches the tradable-assets catalog with balances from business wallet
 * (GET /api/v2/business/:id/wallet/assets / portfolio rows).
 * Used so Buy/Sell/Receive dropdowns show the active business’s balances.
 */

import { formatRandsDisplay, ZAR_RANDS_ZERO } from "./zarDisplayFormat";

function formatZar(n) {
  const v = Number(n);
  if (Number.isNaN(v)) {
    return ZAR_RANDS_ZERO;
  }
  return formatRandsDisplay(v);
}

function formatCrypto(n, symbol) {
  const v = Number(n);
  if (Number.isNaN(v)) {
    return "0.00";
  }
  const upper = (symbol || "").toUpperCase();
  const d = upper === "BTC" ? 6 : 2;
  return v.toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

/**
 * @param {unknown[]|null|undefined} tradableList - from getAllTradingAssets / useCryptoAssets
 * @param {unknown[]|null|undefined} businessRows - from useBusinessWalletAssetsData().assets
 * @returns {unknown[]}
 */
export function mergeTradableWithBusinessWallet(tradableList, businessRows) {
  const list = Array.isArray(tradableList) ? tradableList : [];
  if (!Array.isArray(businessRows) || businessRows.length === 0) {
    return list;
  }

  const bySymbol = new Map();
  for (const r of businessRows) {
    if (!r || typeof r !== "object") continue;
    const sym = String(
      r.asset || r.assetSymbol || r.assetName || ""
    ).toUpperCase();
    if (sym) {
      bySymbol.set(sym, r);
    }
  }

  return list.map((c) => {
    if (!c || typeof c !== "object") return c;
    const key = String(c.asset || c.assetName || "")
      .trim()
      .toUpperCase();
    const b = key ? bySymbol.get(key) : null;
    if (!b) {
      return {
        ...c,
        availableAmount: 0,
        availableAmountFormatted: "0.00",
        valueInZar: 0,
        valueInLocalCurrency: ZAR_RANDS_ZERO,
        formattedAvailableAmount: "0.00",
      };
    }
    const avail = Number(b.availableAmount ?? b.availableBalance ?? 0);
    const fiatEq = Number(b.valueInZar ?? b.fiatEquivalent ?? 0);
    const safeAvail = Number.isFinite(avail) ? avail : 0;
    const isZar = key === "ZAR";
    return {
      ...c,
      availableAmount: safeAvail,
      valueInZar: Number.isFinite(fiatEq) ? fiatEq : 0,
      availableAmountFormatted: isZar
        ? formatZar(safeAvail)
        : formatCrypto(safeAvail, key),
      formattedAvailableAmount: isZar
        ? formatZar(safeAvail)
        : formatCrypto(safeAvail, key),
      valueInLocalCurrency: formatZar(fiatEq),
    };
  });
}
